<template>
  <div class="assignment-tab" data-testid="setup-tasklist__assignment-tab">
    <div class="assignment-tab__header">
      <div class="assignment-tab__title assignment-tab__title--required">Assignment</div>
      <button
        type="button"
        class="assignment-tab__add-btn"
        data-testid="setup-tasklist__add-assignment-btn"
        @click="addAssignment"
      >
        + Add Assignment
      </button>
    </div>

    <div class="assignment-tab__list">
      <AssignmentBlock
        v-for="(assignment, index) in displayAssignments"
        :key="blockKey(assignment, index)"
        :value="assignment"
        :index="index"
        :assignment-number="Number(assignment.assignment_number) || index + 1"
        :active="active"
        :locations="locations"
        :users="users"
        :all-users="allUsers"
        :is-edit="isEdit"
        :chart-of-accounts="chartOfAccounts"
        :available-gl-codes="availableGlCodes"
        :task-type="taskType"
        :show-threshold="showThreshold"
        :show-assign-accounts="showAssignAccounts"
        :removable="displayAssignments.length > 1"
        :expanded="expandedIndex === index"
        :errors="assignmentErrors(index)"
        @toggle="toggleExpanded(index)"
        @input="updateAssignment(index, $event)"
        @remove="removeAssignment(index)"
      />
    </div>

    <p v-if="errors.assignments" class="error--text text-caption mt-2">{{ errors.assignments }}</p>
  </div>
</template>

<script>
import './AssignmentTab.scss';
import AssignmentBlock from './AssignmentBlock.vue';

const emptyAssignment = (num) => ({
  assignment_number: num,
  location_ids: [],
  preparer: [],
  approver: [],
  requires_sign_off: false,
  sign_off_approvers: [],
  enforceThreshold: true,
  auto_approve: false,
  glcodes: [],
});

const cloneAssignment = (a = {}) => ({
  ...a,
  assignment_number: Number(a.assignment_number) || 1,
  location_ids: [...(a.location_ids || [])].map(String),
  preparer: [...(a.preparer || [])].map(String),
  approver: [...(a.approver || [])].map(String),
  requires_sign_off: !!a.requires_sign_off,
  sign_off_approvers: [...(a.sign_off_approvers || [])].map(String),
  glcodes: [...(a.glcodes || a.glcode || [])].map(String),
  enforceThreshold: a.enforceThreshold !== false,
  auto_approve: !!a.auto_approve,
});

export default {
  name: 'AssignmentTab',

  components: { AssignmentBlock },

  props: {
    assignments: {
      type: Array,
      default: () => [],
    },
    /** True only while Assignment tab is visible — gates usersforlocations fetch. */
    active: {
      type: Boolean,
      default: false,
    },
    locations: {
      type: Array,
      default: () => [],
    },
    users: {
      type: Array,
      default: () => [],
    },
    allUsers: {
      type: Array,
      default: () => [],
    },
    isEdit: {
      type: Boolean,
      default: false,
    },
    chartOfAccounts: {
      type: Array,
      default: () => [],
    },
    availableGlCodes: {
      type: Array,
      default: () => [],
    },
    taskType: {
      type: String,
      default: '',
    },
    showThreshold: {
      type: Boolean,
      default: false,
    },
    showAssignAccounts: {
      type: Boolean,
      default: false,
    },
    errors: {
      type: Object,
      default: () => ({}),
    },
  },

  data() {
    return {
      expandedIndex: 0,
    };
  },

  computed: {
    /** Parent is the single source of truth — no local copy that can go stale. */
    displayAssignments() {
      if (this.assignments?.length) {
        return this.assignments.map(cloneAssignment);
      }
      return [emptyAssignment(1)];
    },
  },

  watch: {
    assignments: {
      deep: true,
      handler(val) {
        if (this.expandedIndex >= (val?.length || 1)) {
          this.expandedIndex = Math.max(0, (val?.length || 1) - 1);
        }
      },
    },
  },

  methods: {
    blockKey(assignment, index) {
      return `assignment-${assignment?.assignment_number || index + 1}-${index}`;
    },

    assignmentErrors(index) {
      return {
        location: this.errors[`assignment_${index}`],
        preparer: this.errors[`assignment_${index}_preparer`],
        approver: this.errors[`assignment_${index}_approver`],
        sign_off_approvers: this.errors[`assignment_${index}_sign_off_approvers`],
        glcode: this.errors[`assignment_${index}_gl`],
      };
    },

    toggleExpanded(index) {
      this.expandedIndex = this.expandedIndex === index ? -1 : index;
    },

    emitAssignments(next) {
      this.$emit(
        'update:assignments',
        (next || []).map(cloneAssignment)
      );
    },

    updateAssignment(index, val) {
      const next = this.displayAssignments.map(cloneAssignment);
      this.$set(next, index, cloneAssignment(val));
      this.emitAssignments(next);
    },

    addAssignment() {
      const next = this.displayAssignments.map(cloneAssignment);
      const nextNum = Math.max(...next.map((a) => a.assignment_number || 0), 0) + 1;
      next.push(emptyAssignment(nextNum));
      this.expandedIndex = next.length - 1;
      this.emitAssignments(next);
    },

    removeAssignment(index) {
      const next = this.displayAssignments
        .filter((_, i) => i !== index)
        .map((a, i) => ({
          ...cloneAssignment(a),
          assignment_number: i + 1,
        }));
      if (!next.length) next.push(emptyAssignment(1));
      this.expandedIndex = Math.min(index, next.length - 1);
      this.emitAssignments(next);
    },
  },
};
</script>
